import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, X } from "lucide-react";
import { api } from "../../lib/api";

const STATUSES = ["Needs Help", "Rescue Underway", "Partially Safe", "All Rescued", "Closed"];
const VERIFS = ["Reported — Unconfirmed", "Verified by Paws on Record"];

export const BLANK_RESCUE = {
  title: "", status: "Needs Help", verification: VERIFS[0], city: "", state: "", state_code: "",
  incident_date: "", incident_location: "", animals_description: "", summary: "", source_url: "",
  reporter_handle: "", contact_name: "", contact_phone: "", contact_email: "", photo_url: "",
  photo_source_url: "", photo_attribution: "", photo_caption: "", flyer_url: "", strip_message: "",
  is_strip_active: false, donation_links: [], case_updates: [],
};

const TEXT_FIELDS = [
  ["title", "Title *"], ["city", "City *"], ["state", "State *"], ["state_code", "State code (CA)"],
  ["incident_date", "Incident date * (YYYY-MM-DD)"], ["incident_location", "Location detail"],
  ["animals_description", "Animals (e.g. 8 dogs) *"], ["source_url", "Source post / news URL"],
  ["reporter_handle", "Reported by (handle)"], ["contact_name", "On-the-ground contact"],
  ["contact_phone", "Contact phone"], ["contact_email", "Contact email"],
  ["photo_url", "Photo URL (or /urgent-rescues/...)"], ["photo_source_url", "Photo source URL"],
  ["photo_attribution", "Photo attribution"], ["photo_caption", "Photo caption"], ["flyer_url", "Custom flyer URL"],
];
const inp = "w-full border border-zinc-300 px-2 py-1.5 text-sm outline-none focus:border-zinc-900";

function ListEditor({ label, items, fields, onChange, testId }) {
  const upd = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-bold uppercase tracking-widest">{label}</span>
        <button type="button" onClick={() => onChange([...items, Object.fromEntries(fields.map((f) => [f, ""]))])} className="text-xs inline-flex items-center gap-1 underline" data-testid={`${testId}-add`}><Plus size={11} /> Add</button>
      </div>
      {items.map((it, i) => (
        <div key={i} className="flex gap-2 mb-2 items-start">
          {fields.map((f) => <input key={f} placeholder={f} value={it[f] || ""} onChange={(e) => upd(i, f, e.target.value)} className={inp} data-testid={`${testId}-${f}-${i}`} />)}
          {"verified" in it && <span className={`text-[10px] font-bold uppercase px-1 py-2 ${it.verified ? "text-green-700" : "text-amber-700"}`}>{it.verified ? "Verified" : "Unverified"}</span>}
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="p-2 text-red-600" data-testid={`${testId}-remove-${i}`}><Trash2 size={13} /></button>
        </div>
      ))}
    </div>
  );
}

export default function AdminUrgentRescueForm({ initial, headers, onClose, onSaved }) {
  const isNew = !initial.id;
  const [f, setF] = useState({ ...BLANK_RESCUE, ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    const body = { ...f };
    ["id", "is_published", "help_offer_count", "created_at", "updated_at", "last_contact_sent_at", "last_contact_recipient_count",
      "generated_share_url", "share_image_choice", "share_image_approved", "share_image_url"].forEach((k) => delete body[k]);
    try {
      if (isNew) await api.post("/admin/urgent-rescues", body, { headers });
      else await api.put(`/admin/urgent-rescues/${initial.id}`, body, { headers });
      toast.success(isNew ? "Draft created (not public until you publish)." : "Saved.");
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed — check required fields.");
    } finally { setBusy(false); }
  };

  return (
    <div className="border-2 border-zinc-900 bg-white p-5 mt-4 space-y-4" data-testid="urgent-rescue-form">
      <div className="flex items-center justify-between">
        <div className="font-display font-black text-lg">{isNew ? "New urgent rescue (draft)" : `Edit: ${initial.title}`}</div>
        <button onClick={onClose} data-testid="urgent-form-close"><X size={16} /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <select value={f.status} onChange={(e) => set("status", e.target.value)} className={inp} data-testid="urgent-form-status">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
        <select value={f.verification} onChange={(e) => set("verification", e.target.value)} className={inp} data-testid="urgent-form-verification">{VERIFS.map((s) => <option key={s}>{s}</option>)}</select>
        {TEXT_FIELDS.map(([k, label]) => (
          <label key={k} className="text-[11px] font-bold uppercase tracking-widest">{label}
            <input value={f[k] || ""} onChange={(e) => set(k, e.target.value)} className={`${inp} mt-1 normal-case font-normal tracking-normal`} data-testid={`urgent-form-${k}`} />
          </label>
        ))}
      </div>
      <label className="block text-[11px] font-bold uppercase tracking-widest">Summary *
        <textarea rows={5} value={f.summary} onChange={(e) => set("summary", e.target.value)} className={`${inp} mt-1 normal-case font-normal tracking-normal`} data-testid="urgent-form-summary" />
      </label>
      <div className="border border-red-600 bg-red-50 p-3 space-y-2">
        <label className="block text-[11px] font-bold uppercase tracking-widest">Emergency strip message
          <input value={f.strip_message || ""} onChange={(e) => set("strip_message", e.target.value)} className={`${inp} mt-1 normal-case font-normal tracking-normal`} data-testid="urgent-form-strip_message" />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!f.is_strip_active} onChange={(e) => set("is_strip_active", e.target.checked)} data-testid="urgent-form-strip-active" />
          Show on the red strip at the top of every page (only once published)
        </label>
      </div>
      <ListEditor label="Rescue donation links (verify each one in Tools)" items={f.donation_links || []} fields={["org_name", "url", "note"]} onChange={(v) => set("donation_links", v)} testId="urgent-form-donations" />
      <ListEditor label="Case updates" items={f.case_updates || []} fields={["date", "text"]} onChange={(v) => set("case_updates", v)} testId="urgent-form-updates" />
      <button onClick={save} disabled={busy} className="btn-danger disabled:opacity-60" data-testid="urgent-form-save">
        {busy && <Loader2 size={14} className="animate-spin" />} {isNew ? "Create draft" : "Save changes"}
      </button>
    </div>
  );
}
